import type { PatternKind, ShapePattern } from "./schema";

/**
 * Vector patterns over shape fills ("Hintergründe", docs/04-dokumentformat.md).
 * The motifs are one plain SVG path covering the shape (rotated around its
 * centre, clipped to its outline), so PDFs and the CMYK print PDF keep them as
 * vectors – no SVG <pattern> tiles, which Chromium may rasterise.
 */

export const PATTERN_LABELS: Record<PatternKind, string> = {
  dots: "Punkte",
  stripes: "Streifen",
  grid: "Karo",
  waves: "Wellen",
  zigzag: "Zickzack",
  circles: "Kreise",
  triangles: "Dreiecke",
  confetti: "Konfetti",
};

export interface PatternGeometry {
  d: string;
  mode: "fill" | "stroke";
  strokeWidth: number;
  /** Tile size actually used (grown when the shape would need too many tiles). */
  size: number;
}

/** Upper bound of tiles per pattern (keeps paths and PDFs small). */
export const PATTERN_MAX_TILES = 6000;

const r1 = (value: number) => Math.round(value * 10) / 10;

/** Deterministic pseudo-random numbers in [0, 1) per tile. */
function random(i: number, j: number, k: number): number {
  let h = (i * 374761393 + j * 668265263 + k * 2147483647) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function circle(cx: number, cy: number, r: number): string {
  return `M${r1(cx - r)} ${r1(cy)}a${r1(r)} ${r1(r)} 0 1 0 ${r1(2 * r)} 0a${r1(r)} ${r1(r)} 0 1 0 ${r1(-2 * r)} 0Z`;
}

/**
 * Path of a pattern for a w×h shape, in the shape's coordinates before the
 * rotation (`rotate(rotation, w/2, h/2)`); it covers the shape at any angle.
 */
export function patternGeometry(
  pattern: ShapePattern,
  width: number,
  height: number,
): PatternGeometry {
  // The area to cover before the rotation: the shape's box turned back.
  const rad = (pattern.rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const coverW = width * cos + height * sin;
  const coverH = width * sin + height * cos;
  let size = Math.max(4, pattern.size);
  const tiles = (tile: number) => (Math.ceil(coverW / tile) + 2) * (Math.ceil(coverH / tile) + 2);
  while (tiles(size) > PATTERN_MAX_TILES) size *= 1.1;
  const nx = Math.ceil(coverW / size) + 2;
  const n = Math.ceil(coverH / size) + 2;
  const left = width / 2 - (nx * size) / 2;
  const top = height / 2 - (n * size) / 2;
  const right = left + nx * size;
  const s = size;
  const parts: string[] = [];
  let mode: PatternGeometry["mode"] = "fill";
  let strokeWidth = 0;
  switch (pattern.kind) {
    case "dots":
      for (let j = 0; j < n; j++)
        for (let i = 0; i < nx; i++) {
          const shift = j % 2 === 0 ? 0 : s / 2;
          parts.push(circle(left + i * s + s / 2 + shift, top + j * s + s / 2, s * 0.18));
        }
      break;
    case "stripes":
      for (let j = 0; j < n; j++) {
        const y = top + j * s;
        parts.push(`M${r1(left)} ${r1(y)}H${r1(right)}V${r1(y + s * 0.45)}H${r1(left)}Z`);
      }
      break;
    case "grid": {
      const t = s * 0.08;
      for (let j = 0; j < n; j++) {
        const y = top + j * s;
        parts.push(`M${r1(left)} ${r1(y)}H${r1(right)}V${r1(y + t)}H${r1(left)}Z`);
      }
      const bottom = top + n * s;
      for (let i = 0; i < nx; i++) {
        const x = left + i * s;
        parts.push(`M${r1(x)} ${r1(top)}V${r1(bottom)}H${r1(x + t)}V${r1(top)}Z`);
      }
      break;
    }
    case "waves":
    case "zigzag": {
      mode = "stroke";
      strokeWidth = s * 0.08;
      const a = s * 0.18;
      for (let j = 0; j < n; j++) {
        const y = top + j * s + s / 2;
        const step =
          pattern.kind === "waves"
            ? `q${r1(s / 4)} ${r1(-a)} ${r1(s / 2)} 0t${r1(s / 2)} 0`
            : `l${r1(s / 2)} ${r1(-a)}l${r1(s / 2)} ${r1(a)}`;
        parts.push(`M${r1(left)} ${r1(y)}${step.repeat(nx)}`);
      }
      break;
    }
    case "circles":
      mode = "stroke";
      strokeWidth = s * 0.07;
      for (let j = 0; j < n; j++)
        for (let i = 0; i < nx; i++)
          parts.push(circle(left + i * s + s / 2, top + j * s + s / 2, s * 0.3));
      break;
    case "triangles":
      for (let j = 0; j < n; j++)
        for (let i = 0; i < nx; i++) {
          const x = left + i * s + (j % 2 === 0 ? 0 : s / 2);
          const y = top + j * s;
          parts.push(
            `M${r1(x + s / 2)} ${r1(y + s * 0.22)}L${r1(x + s * 0.8)} ${r1(y + s * 0.74)}L${r1(x + s * 0.2)} ${r1(y + s * 0.74)}Z`,
          );
        }
      break;
    case "confetti":
      for (let j = 0; j < n; j++)
        for (let i = 0; i < nx; i++) {
          const cx = left + (i + 0.2 + random(i, j, 1) * 0.6) * s;
          const cy = top + (j + 0.2 + random(i, j, 2) * 0.6) * s;
          if (random(i, j, 3) < 0.35) {
            parts.push(circle(cx, cy, s * 0.07));
            continue;
          }
          const angle = random(i, j, 4) * Math.PI;
          const hw = s * 0.14;
          const hh = s * 0.045;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const corners = [
            [-hw, -hh],
            [hw, -hh],
            [hw, hh],
            [-hw, hh],
          ].map(([x, y]) => `${r1(cx + x! * cos - y! * sin)} ${r1(cy + x! * sin + y! * cos)}`);
          parts.push(`M${corners.join("L")}Z`);
        }
      break;
  }
  return { d: parts.join(""), mode, strokeWidth: r1(strokeWidth), size };
}
